import { DroneFlightEntry } from '../data-models/drone-flight-entry.model';

export const DEFAULT_RATE_UNIT = 'oz/ac';

/**
 * Parse a compound rate unit string like "oz/ac" into unit and perUnit parts
 */
export function parseRateUnit(rateUnit: string): { unit: string; perUnit: string } {
  const parts = rateUnit.split('/');
  return { unit: parts[0] || 'oz', perUnit: parts[1] || 'ac' };
}

/**
 * Build a display rate string from value and unit
 * e.g., (22, "oz/ac") -> "22 oz/ac"
 */
export function buildRateString(value: number, rateUnit: string): string {
  return `${value} ${rateUnit}`;
}

/** Conversion factors: unit → fluid ounces */
const OZ_PER: Record<string, number> = {
  oz: 1,
  pt: 16,
  qt: 32,
  gal: 128,
};

/**
 * Convert a value from one liquid unit to another.
 * Returns null if either unit is unrecognized (e.g., lb).
 */
export function convertUnit(value: number, fromUnit: string, toUnit: string): number | null {
  const from = OZ_PER[fromUnit];
  const to = OZ_PER[toUnit];
  if (from == null || to == null) return null;
  return (value * from) / to;
}

/**
 * Convert a value in fluid ounces to the most human-friendly unit.
 *   >= 128 oz → gal
 *   >= 32 oz  → qt
 *   >= 16 oz  → pt
 *   else      → oz
 */
export function formatBestUnit(ozValue: number): { value: number; unit: string } {
  if (ozValue >= 128) return { value: ozValue / 128.0, unit: 'gal' };
  if (ozValue >= 32)  return { value: ozValue / 32.0, unit: 'qt' };
  if (ozValue >= 16)  return { value: ozValue / 16.0, unit: 'pt' };
  return { value: ozValue, unit: 'oz' };
}

/**
 * Normalize a value + unit to fluid ounces.
 * Returns null for unrecognized units (e.g., lb).
 */
export function toOz(value: number, unit: string): number | null {
  const factor = OZ_PER[unit];
  return factor != null ? value * factor : null;
}

/**
 * Helper to parse rate string into value and unit
 * e.g., "22 oz/acre" -> { value: 22, unit: "oz", per: "ac" }
 */
export function parseRate(rateString: string): { value: number; unit: string; per: string } | null {
  const trimmedRate = rateString.trim();

  // Check for bare number (no unit)
  if (trimmedRate.match(/^(\d+(?:\.\d+)?|\.\d+)$/)) {
    const { unit, perUnit } = parseRateUnit(DEFAULT_RATE_UNIT);
    return { value: parseFloat(trimmedRate), unit, per: perUnit };
  }

  // Unit "/" per-unit, e.g. "32 oz/acre", "15oz/ac", "22oz / acre", ".5 lb/ac"
  // Also allows a repeated "/ac" saved by an earlier bug, e.g. "23 gal/ac/ac" -> gal/ac
  const slashMatch = trimmedRate.match(/^(\d+(?:\.\d+)?|\.\d+)\s*(oz|pt|qt|gal|lb)\s*\/\s*(acre|ac)(?:\/ac)?$/i);
  if (slashMatch) {
    return { value: parseFloat(slashMatch[1]), unit: slashMatch[2].toLowerCase(), per: 'ac' };
  }

  // Unit with the word "per", e.g. "16 oz. Per acre" -> oz/ac
  const perWordMatch = trimmedRate.match(/^(\d+(?:\.\d+)?|\.\d+)\s*(oz|pt|qt|gal|lb)\.?\s+per\s+acre$/i);
  if (perWordMatch) {
    return { value: parseFloat(perWordMatch[1]), unit: perWordMatch[2].toLowerCase(), per: 'ac' };
  }

  // Unit with no per-unit, e.g. "22 oz", "11 gals" -> assume per acre ("gals" -> "gal")
  const unitOnlyMatch = trimmedRate.match(/^(\d+(?:\.\d+)?|\.\d+)\s*(oz|pt|qt|gal|lb)s?$/i);
  if (unitOnlyMatch) {
    return { value: parseFloat(unitOnlyMatch[1]), unit: unitOnlyMatch[2].toLowerCase(), per: 'ac' };
  }

  // Anything else is not a known format
  return null;
}

/**
 * Parse a rate string into a compound unit string for the dropdown
 * e.g., "22 oz/acre" -> { value: 22, rateUnit: "oz/ac" }
 */
export function parseRateToStructured(rateString: string): { value: number; rateUnit: string } | null {
  const parsed = parseRate(rateString);
  if (!parsed) return null;
  return {
    value: parsed.value,
    rateUnit: `${parsed.unit}/${parsed.per}`
  };
}

/**
 * Calculate chemical usage based on acreage and rate.
 * Multiplies rate × acreage in the rate's native unit, then
 * normalizes to the most readable liquid unit.
 */
export function calculateChemicalUsage(acreage: number, rateString: string): string {
  const parsed = parseRate(rateString);
  if (!parsed) return 'N/A';

  const totalInRateUnit = acreage * parsed.value;

  // Convert to oz, pick best display unit, convert back
  const totalOz = toOz(totalInRateUnit, parsed.unit);
  if (totalOz == null) {
    // Non-liquid unit (lb, etc.) — return as-is
    return `${totalInRateUnit.toFixed(2)} ${parsed.unit}`;
  }

  const best = formatBestUnit(totalOz);
  return `${best.value.toFixed(2)} ${best.unit}`;
}

/**
 * Calculate totals across all drone flights.
 * Normalizes all amounts to fluid ounces before summing,
 * then formats back to the most readable unit.
 */
export function calculateRecordTotals(droneFlights: DroneFlightEntry[]): {
  totalAcreage: number;
  totalChemicalUsage: Map<string, { name: string; totalAmount: string }>;
} {
  const totalAcreage = droneFlights.reduce((sum, df) => sum + df.acreageSprayed, 0);

  // Aggregate chemical usage across all drones (liquids in fl oz; for lb chemicals totalOz holds pounds)
  const chemicalTotals = new Map<string, { name: string; totalOz: number; isLb: boolean }>();

  droneFlights.forEach(df => {
    df.chemicalUsage.forEach(cu => {
      const parsed = parseRate(cu.amountUsed);
      if (parsed) {
        // lb is weight: add pounds as-is; liquids are converted to fl oz
        const oz = parsed.unit === 'lb' ? parsed.value : toOz(parsed.value, parsed.unit);
        if (oz == null) return; // skip unknown units
        const existing = chemicalTotals.get(cu.appliedChemicalId);
        if (existing) {
          existing.totalOz += oz;
        } else {
          chemicalTotals.set(cu.appliedChemicalId, {
            name: cu.chemicalName,
            totalOz: oz,
            isLb: parsed.unit === 'lb'
          });
        }
      }
    });
  });

  const totalChemicalUsage = new Map<string, { name: string; totalAmount: string }>();
  chemicalTotals.forEach((value, key) => {
    const best = formatBestUnit(value.totalOz);
    totalChemicalUsage.set(key, {
      name: value.name,
      totalAmount: value.isLb
        ? `${value.totalOz.toFixed(2)} lb`
        : `${best.value.toFixed(2)} ${best.unit}`
    });
  });

  return { totalAcreage, totalChemicalUsage };
}
